import React, { useId, ComponentPropsWithRef } from "react";

// Note: <select> renders ~1px taller than <input> — Chrome forces
// `line-height: normal` on single-line selects at UA level, so it cannot be
// equalised in CSS. Filter rows use `items-end` so the baselines still align.
const FIELD =
  "border-hairline/70 bg-surface focus:border-brand/70 w-full rounded-xl border px-4 py-2.5 " +
  "text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:outline-none";

const LABEL = "text-xs font-medium tracking-wide text-zinc-500 uppercase";

type Props = ComponentPropsWithRef<"input"> & {
  label?: string;
  error?: string | null;
};

export function TextInput({
  label,
  error,
  className = "",
  id,
  ...props
}: Props) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className={LABEL}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        className={`${FIELD} ${error ? "border-red-500/60" : ""} ${className}`}
        {...props}
      />
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

type SelectProps = ComponentPropsWithRef<"select"> & {
  label?: string;
  error?: string | null;
};

export function Select({
  label,
  error,
  className = "",
  id,
  children,
  ...props
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={selectId} className={LABEL}>
          {label}
        </label>
      )}
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        className={`${FIELD} ${error ? "border-red-500/60" : ""} ${className}`}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

type TextareaProps = ComponentPropsWithRef<"textarea"> & {
  label?: string;
  error?: string | null;
};

export function Textarea({
  label,
  error,
  className = "",
  id,
  ...props
}: TextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={textareaId} className={LABEL}>
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        aria-invalid={error ? true : undefined}
        className={`${FIELD} ${error ? "border-red-500/60" : ""} ${className}`}
        {...props}
      />
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
